// apps/client/src/router/index.tsx
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import ClientLayout from "@/layouts/ClientLayout";

// Auth pages
const LoginPage = lazy(() =>
  import("@/pages/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const OTPVerifyPage = lazy(() => import("@/pages/auth/OTPVerifyPage"));
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));

// Error pages
const NotFoundPage = lazy(() => import("@/pages/error/NotFoundPage"));
const UnauthorizedPage = lazy(() => import("@/pages/error/UnauthorizedPage"));
const ServerErrorPage = lazy(() => import("@/pages/error/ServerErrorPage"));
const MaintenancePage = lazy(() => import("@/pages/error/MaintenancePage"));

// Public pages
const HomePage = lazy(() => import("@/pages/Home"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));

const ContactPage = lazy(() => import("@/pages/ContactPage"));
const TrackPage = lazy(() => import("@/pages/TrackPage"));
const PublicLayout = lazy(() => import("@/components/layout/PublicLayout"));
// apps/client/src/router/index.tsx

// with the other public page imports:
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));

// Dashboard
const DashboardPage = lazy(() =>
  import("@/pages/dashboard/ClientDashboard").then((m) => ({
    default: m.DashboardPage,
  })),
);

// Order pages
const OrdersListPage = lazy(() => import("@/pages/orders/OrdersListPage"));
const OrderDetailPage = lazy(() => import("@/pages/orders/OrderDetailPage"));
const NewOrderPage = lazy(() => import("@/pages/orders/NewOrderPage"));

// Invoice + Payment pages
const InvoicesListPage = lazy(
  () => import("@/pages/invoices/InvoicesListPage"),
);
const InvoiceDetailPage = lazy(
  () => import("@/pages/invoices/InvoiceDetailPage"),
);
const PaymentsPage = lazy(() => import("@/pages/payments/PaymentsPage"));
const ProfilePage = lazy(() => import("@/pages/profile/ProfilePage"));
const DocumentsPage = lazy(() => import("@/pages/documents/DocumentsPage"));
const ClientLRDetailPage = lazy(
  () => import("@/pages/lorry-receipts/LRDetailPage"),
);
const LRListPage = lazy(() => import("@/pages/lorry-receipts/LRListPage"));

// Tracking
const TrackingPage = lazy(() => import("@/pages/TrackingPage"));
const ClientFleetMapPage = lazy(
  () => import("@/pages/fleet/ClientFleetMapPage"),
);
const TeamPage = lazy(() => import("@/pages/team/TeamPage"));
const AcceptInvitePage = lazy(() => import("@/pages/auth/AcceptInvitePage"));

const VehicleHireContractsPage = lazy(
  () => import("@/pages/vehicle-hire/VehicleHireContractsPage"),
);

const router = createBrowserRouter([
  // ── Public home ──────────────────────────────────────────────────────────────
  {
    path: "/",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <HomePage />
      </Suspense>
    ),
  },

  // ── Public pages (Navbar + Footer) ───────────────────────────────────────────
  {
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <PublicLayout />
      </Suspense>
    ),
    children: [
      {
        path: "/about",
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <AboutPage />
          </Suspense>
        ),
      },
      // ...services, contact, track stay as-is...
      {
        path: "/legal/privacy",
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <PrivacyPolicyPage />
          </Suspense>
        ),
      },
      {
        path: "/services",
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <Navigate to="/#services" replace />
          </Suspense>
        ),
      },
      {
        path: "/contact",
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <ContactPage />
          </Suspense>
        ),
      },
      {
        path: "/track",
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <TrackPage />
          </Suspense>
        ),
      },
      {
        path: "/track/:lrNumber",
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <TrackPage />
          </Suspense>
        ),
      },
    ],
  },

  // ── Auth routes ───────────────────────────────────────────────────────────────
  {
    path: "/login",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/register",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/verify-otp",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <OTPVerifyPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/forgot-password",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/reset-password",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/accept-invite",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <AcceptInvitePage />
      </Suspense>
    ),
  },

  // ── Protected routes — all tiers (Owner/Branch Manager/Staff) ────────────────
  {
    element: (
      <ProtectedRoute
        allowedRoles={["client", "admin", "super_admin", "agent"]}
      />
    ),
    children: [
      {
        element: <ClientLayout />,
        children: [
          {
            path: "/dashboard",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: "/orders",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <OrdersListPage />
              </Suspense>
            ),
          },
          {
            path: "/orders/new",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <NewOrderPage />
              </Suspense>
            ),
          },
          {
            path: "/orders/:id",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <OrderDetailPage />
              </Suspense>
            ),
          },
          {
            path: "/profile",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <ProfilePage />
              </Suspense>
            ),
          },

          {
            path: "/lorry-receipts",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <LRListPage />
              </Suspense>
            ),
          },
          {
            path: "/lorry-receipts/:id",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <ClientLRDetailPage />
              </Suspense>
            ),
          },
          {
            path: "/shipments",
            element: <Navigate to="/orders" replace />,
          },
          {
            path: "/tracking/:shipmentId",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <TrackingPage />
              </Suspense>
            ),
          },
          {
            path: "/fleet",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <ClientFleetMapPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // ── Day 87: Invoices — Owner + Branch Manager only ───────────────────────────
  {
    element: (
      <ProtectedRoute
        allowedRoles={["client", "admin", "super_admin", "agent"]}
        requireClientFeature="invoices"
      />
    ),
    children: [
      {
        element: <ClientLayout />,
        children: [
          {
            path: "/invoices",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <InvoicesListPage />
              </Suspense>
            ),
          },
          {
            path: "/invoices/:id",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <InvoiceDetailPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // ── Day 87: Payments — Owner + Branch Manager only ───────────────────────────
  {
    element: (
      <ProtectedRoute
        allowedRoles={["client", "admin", "super_admin", "agent"]}
        requireClientFeature="payments"
      />
    ),
    children: [
      {
        element: <ClientLayout />,
        children: [
          {
            path: "/payments",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <PaymentsPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // ── Day 87: Documents — Owner + Branch Manager only ──────────────────────────
  {
    element: (
      <ProtectedRoute
        allowedRoles={["client", "admin", "super_admin", "agent"]}
        requireClientFeature="documents"
      />
    ),
    children: [
      {
        element: <ClientLayout />,
        children: [
          {
            path: "/documents",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <DocumentsPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // Daily MIS page removed from the client app: the client receives the
  // Daily MIS Excel by email every day at 10 AM. MisHistoryPage.tsx is kept.

  // ── Vehicle Hire Contracts — Owner + Branch Manager only ──────────────────────
  {
    element: (
      <ProtectedRoute
        allowedRoles={["client", "admin", "super_admin", "agent"]}
        requireClientFeature="vehicleHireContracts"
      />
    ),
    children: [
      {
        element: <ClientLayout />,
        children: [
          {
            path: "/vehicle-hire-contracts",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <VehicleHireContractsPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // ── Day 87: Team management — Owner-only within the client tier ──────────────
  {
    element: (
      <ProtectedRoute
        allowedRoles={["client", "admin", "super_admin", "agent"]}
        requireClientFeature="team"
      />
    ),
    children: [
      {
        element: <ClientLayout />,
        children: [
          {
            path: "/team",
            element: (
              <Suspense fallback={<PageSkeleton />}>
                <TeamPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // ── Error pages ───────────────────────────────────────────────────────────────
  {
    path: "/500",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <ServerErrorPage />
      </Suspense>
    ),
  },
  {
    path: "/maintenance",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <MaintenancePage />
      </Suspense>
    ),
  },
  {
    path: "/unauthorized",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <UnauthorizedPage />
      </Suspense>
    ),
  },
  {
    path: "*",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
// Day 67 complete
// Day 87: /invoices, /payments, /documents moved into their own
// requireClientFeature-protected blocks — Staff hitting these directly now
// gets a clean /unauthorized redirect instead of a broken page shell.
